'use client';

import React, { useCallback } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import {
  Typography,
  IdView,
  CustomHeroButton,
  UniversalTable,
  ResponsiveModal,
} from '../../../../components';
import { IcDate } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { RevshareBill } from '../types';

interface PayBillModalProps {
  isOpen: boolean;
  bill: RevshareBill | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const PayBillModal: React.FC<PayBillModalProps> = ({
  isOpen,
  bill,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const { t } = useTranslations();

  const columns = [
    { key: 'id', name: 'ID' },
    { key: 'amount', name: t('client.revshare.bills.table.columns.amount') },
    { key: 'date', name: t('client.revshare.bills.table.columns.date') },
  ];

  const parseDate = (value?: string): Date | null => {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const normalized = trimmed.replace(' ', 'T');
    const hasTimeZone = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(normalized);

    if (hasTimeZone) {
      const parsed = new Date(normalized);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const [datePart, timePart = ''] = normalized.split('T');
    const [yearStr, monthStr, dayStr] = datePart.split('-');
    const [timeWithoutMs] = timePart.split('.');
    const [hourStr = '0', minuteStr = '0', secondStr = '0'] = (timeWithoutMs || '').split(':');

    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    const day = Number(dayStr);
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    const second = Number(secondStr);

    if ([year, monthIndex, day, hour, minute, second].some((val) => Number.isNaN(val))) {
      return null;
    }

    const utcTimestamp = Date.UTC(year, monthIndex, day, hour, minute, second);
    return new Date(utcTimestamp);
  };

  const formatDateTime = (dateString: string): string => {
    const date = parseDate(dateString);
    if (!date) return '—';

    const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    const dateStr = date.toLocaleDateString('ru-RU');
    return `${time}, ${dateStr}`;
  };

  const renderCell = useCallback((item: RevshareBill, columnKey: string) => {
    const displayId = item._id.slice(-4);
    const amount = typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount;

    switch (columnKey) {
      case 'id':
        return <IdView textToCopy={item._id}>{displayId}</IdView>;
      case 'amount':
        return (
          <Typography variant="body2" className="md:font-semibold">
            ${amount.toFixed(2)}
          </Typography>
        );
      case 'date':
        return (
          <div className="flex items-center gap-1">
            <IcDate className="w-3.5 h-3.5 text-auth-muted" />
            <Typography variant="body3" className="text-auth-muted">
              {formatDateTime(item.created_at)}
            </Typography>
          </div>
        );
      default:
        return null;
    }
  }, []);

  if (!bill) return null;

  const tableData = [{ ...bill, id: bill._id }];

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      classNames={{ base: 'md:max-w-[35.25rem]' }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <Typography variant="h6" className="md:font-semibold">
            {t('client.revshare.bills.modal.title')}
          </Typography>
        </ModalHeader>
        <ModalBody className="pb-0">
          <UniversalTable data={tableData} columns={columns} renderCell={renderCell} />
        </ModalBody>
        <ModalFooter className="pt-0">
          <CustomHeroButton
            title={t('client.revshare.bills.modal.cancel')}
            onPress={onClose}
            customStyles={{
              background: '#FDD0DF',
              color: '#F31260',
            }}
          />
          <CustomHeroButton
            title={t('client.revshare.bills.modal.confirm')}
            onPress={onConfirm}
            isLoading={isLoading}
            variant="primary"
          />
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default PayBillModal;
